/**
 * Inventory Intake Panel Component (Batch Workflow)
 * 
 * Batch-based workflow: adds items to a review batch before submitting to inventory.
 * Allows editing, reviewing, and auditing entries before final commit.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { colors, spacing, typography, typographyFlat, borderRadius, transitions, colorExtensions } from '../../styles/tokens';
import {
  ProductSearchAutocomplete,
  Product,
  ProductSearchAutocompleteHandle,
  CatalogGame,
} from './ProductSearchAutocomplete';
import { InventoryCopyLookupLink } from './InventoryCopyLookupLink';
import { CardImage } from '../card/CardImage';
import { Button } from '../base/Button';
import { IntakePricingPanel } from '../pricing/IntakePricingPanel';
import { ManapoolSalesChart, ManapoolSalePoint } from '../pricing/ManapoolSalesChart';
import { PolicyPriceDebug } from '../pricing/PolicySignalsPanel';
import {
  CONDITIONS,
  FINISHES,
  LANGUAGES,
  FinishValue,
  finishFromCopy,
  isFoilFromFinish,
} from '../../lib/cardVariantOptions';
import {
  chunkArray,
  distinctIntakePreviewRequests,
  itemNeedsPolicyRehydrate,
  mapWithConcurrency,
  mergeIntakePricing,
  mergeItemsWithPreviews,
} from '../../lib/batchItemPricing';

type ApiResponse<T = unknown> = { data: T };

interface ApiClient {
  get: <T = unknown>(url: string, config?: unknown) => Promise<ApiResponse<T>>;
  post: <T = unknown>(url: string, data?: unknown, config?: unknown) => Promise<ApiResponse<T>>;
  patch: <T = unknown>(url: string, data?: unknown, config?: unknown) => Promise<ApiResponse<T>>;
  delete: <T = unknown>(url: string, config?: unknown) => Promise<ApiResponse<T>>;
}

interface WindowWithWebkitAudioContext extends Window {
  webkitAudioContext?: typeof AudioContext;
}

// Price tier sounds (using Web Audio API)
const playPriceSound = (price: number | undefined) => {
  if (!price) return;
  
  try {
    const AudioContextCtor = window.AudioContext || (window as WindowWithWebkitAudioContext).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }
    const audioContext = new AudioContextCtor();
    const now = audioContext.currentTime;
    
    if (price >= 20) {
      // "Cha-ching" sound for $20+
      const osc1 = audioContext.createOscillator();
      osc1.frequency.setValueAtTime(1200, now);
      osc1.frequency.setValueAtTime(800, now + 0.1);
      const gain1 = audioContext.createGain();
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.setTargetAtTime(0, now + 0.2, 0.05);
      osc1.connect(gain1);
      gain1.connect(audioContext.destination);
      osc1.start(now);
      osc1.stop(now + 0.2);
      
      // Second tone
      const osc2 = audioContext.createOscillator();
      osc2.frequency.setValueAtTime(1600, now + 0.1);
      osc2.frequency.setValueAtTime(1200, now + 0.25);
      const gain2 = audioContext.createGain();
      gain2.gain.setValueAtTime(0.3, now + 0.1);
      gain2.gain.setTargetAtTime(0, now + 0.3, 0.05);
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.start(now + 0.1);
      osc2.stop(now + 0.3);
    } else if (price >= 5) {
      // Money bag rustle for $5+
      const noise = audioContext.createBufferSource();
      const buffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.3, audioContext.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < buffer.length; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      noise.buffer = buffer;
      
      const filter = audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 2000;
      
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.setTargetAtTime(0, now, 0.1);
      
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(audioContext.destination);
      noise.start(now);
      noise.stop(now + 0.3);
    } else if (price >= 1) {
      // Coin drop for $1+
      const osc = audioContext.createOscillator();
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.setTargetAtTime(200, now, 0.1);
      
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.setTargetAtTime(0, now + 0.3, 0.1);
      
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    }
  } catch (err) {
    console.warn('Audio playback unavailable');
  }
};

const formatApiError = (err: unknown, fallback: string) => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const typedItem = item as { loc?: unknown; msg?: string };
        const loc = Array.isArray(typedItem.loc) ? typedItem.loc.join('.') : '';
        const msg = typedItem.msg || 'Validation error';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join('; ');
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail) {
    return JSON.stringify(detail);
  }
  return err instanceof Error ? err.message : fallback;
};

interface BatchItem {
  id: string;
  mtgjson_uuid?: string | null;
  tcgplayer_id?: string | null;
  catalog_source?: string | null;
  product_line?: string | null;
  card_name: string;
  set_name?: string;
  card_number?: string;
  condition: string;
  is_foil: boolean;
  quantity: number;
  location_bin?: string;
  language: string;
  printing: string;
  estimated_cost?: number;
  image_url?: string;
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
  tcg_export_market?: number | null;
  tcg_low?: number | null;
  manapool_low?: number | null;
  calculated_offer?: number | null;
  price_source?: string | null;
  manapool_recent_sales?: ManapoolSalePoint[] | null;
  policy_price?: number | null;
  policy_price_debug?: PolicyPriceDebug | null;
  offer_ck_floored?: boolean | null;
}

interface BatchSummary {
  id: string;
  name: string;
  status: string;
  items?: BatchItem[];
}

interface BatchListResponseItem {
  id: string;
}

interface PricingPreview {
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
  preferred_market?: number | null;
  calculated_offer?: number | null;
  available_finishes?: FinishValue[];
  selected_finish?: FinishValue;
  tcg_export_market?: number | null;
  tcg_low?: number | null;
  manapool_low?: number | null;
  price_source?: string | null;
  manapool_recent_sales?: ManapoolSalePoint[] | null;
  policy_price?: number | null;
  policy_price_debug?: PolicyPriceDebug | null;
  offer_ck_floored?: boolean | null;
}

function isTcgcsvPricing(
  value?: { price_source?: string | null; catalog_source?: string | null; tcgplayer_id?: string | null } | null,
): boolean {
  if (!value) return false;
  if ((value.price_source || '').toLowerCase() === 'tcgcsv') return true;
  if ((value.catalog_source || '').toLowerCase() === 'tcgcsv') return true;
  return Boolean(value.tcgplayer_id);
}

const catalogIdentityFromProduct = (product: Product) => {
  const isTcgcsv = product.catalog_source === 'tcgcsv' || Boolean(product.tcgplayer_id);
  if (isTcgcsv) {
    return {
      tcgplayer_id: product.tcgplayer_id || product.product_id,
      catalog_source: 'tcgcsv' as const,
      product_line: product.product_line,
    };
  }
  return { mtgjson_uuid: product.product_id };
};

const catalogIdentityFromItem = (
  item: Pick<BatchItem, 'mtgjson_uuid' | 'tcgplayer_id' | 'catalog_source' | 'product_line'>,
) => {
  if (item.tcgplayer_id && !item.mtgjson_uuid) {
    return {
      tcgplayer_id: item.tcgplayer_id,
      catalog_source: item.catalog_source || 'tcgcsv',
      product_line: item.product_line,
    };
  }
  return { mtgjson_uuid: item.mtgjson_uuid };
};

const ACTIVE_BATCH_STORAGE_KEY = 'inventory-active-batch-id';

interface InventoryIntakePanelProps {
  onInventoryCreated?: () => void;
  /** Defaults the search tab. Does not select or fork the draft batch. */
  initialGame?: CatalogGame;
}

export const InventoryIntakePanel: React.FC<InventoryIntakePanelProps> = ({
  onInventoryCreated,
  initialGame,
}) => {
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<ProductSearchAutocompleteHandle>(null);
  const formatPrice = (value?: number | null) => (value != null ? `$${value.toFixed(2)}` : '—');

  // Batch state
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const [batchName, setBatchName] = useState('');
  const [acquisitionMode, setAcquisitionMode] = useState<'market_offer' | 'pack_open'>('market_offer');
  const [packCost, setPackCost] = useState('');
  const [cardsPerPack, setCardsPerPack] = useState('15');
  
  // Defaults that can be pinned across product selections (stack intake)
  const [defaultCondition, setDefaultCondition] = useState('NM');
  const [defaultFinish, setDefaultFinish] = useState<FinishValue>('normal');
  const [defaultLanguage, setDefaultLanguage] = useState('EN');
  const [pinCondition, setPinCondition] = useState(false);
  const [pinFinish, setPinFinish] = useState(false);
  const [pinLanguage, setPinLanguage] = useState(false);
  
  // Selected product and form state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [finish, setFinish] = useState<FinishValue>('normal');
  const isFoil = isFoilFromFinish(finish);
  const [condition, setCondition] = useState('NM');
  const [quantity, setQuantity] = useState(1);
  const [locationBin, setLocationBin] = useState('');
  const [language, setLanguage] = useState('EN');
  const [isProductFlipped, setIsProductFlipped] = useState(false);
  const [pricingPreview, setPricingPreview] = useState<PricingPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [savingBatchItemIds, setSavingBatchItemIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const availableFinishes = pricingPreview?.available_finishes
    ?? selectedProduct?.available_finishes
    ?? ['normal', 'foil', 'etched'];
  const normalDisabled = !availableFinishes.includes('normal');
  const foilDisabled = !availableFinishes.includes('foil');
  const etchedDisabled = !availableFinishes.includes('etched');
  const signedDisabled = !availableFinishes.includes('signed');
  const reverseHoloDisabled = !availableFinishes.includes('reverse_holofoil');
  const showSignedFinish = availableFinishes.includes('signed');
  const showReverseHoloFinish = availableFinishes.includes('reverse_holofoil');

  const rememberBatchId = useCallback((nextBatchId: string | null) => {
    if (nextBatchId) {
      localStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, nextBatchId);
      setBatchId(nextBatchId);
      return;
    }

    localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    setBatchId(null);
  }, []);

  const createNewBatch = useCallback(async (apiClient: ApiClient) => {
    const createResponse = await apiClient.post('/batches');
    const createData = createResponse.data as { id: string; name?: string };
    rememberBatchId(createData.id);
    setBatchName(createData.name ?? '');
    setBatchItems([]);
    return createData.id;
  }, [rememberBatchId]);

  const hydrateBatch = useCallback((batch: BatchSummary) => {
    // Policy fields come from GET /batches/{id} (live preview helper, not DB columns).
    rememberBatchId(batch.id);
    setBatchName(batch.name ?? '');
    setBatchItems(batch.items || []);
  }, [rememberBatchId]);

  const rehydrateBatchPolicy = useCallback(async (apiClient: ApiClient, items: BatchItem[]) => {
    if (items.length === 0) {
      return;
    }
    const missing = items.filter(itemNeedsPolicyRehydrate);
    if (missing.length === 0) {
      return;
    }

    const requests = distinctIntakePreviewRequests(missing);
    const previews: Array<PricingPreview & {
      mtgjson_uuid?: string | null;
      tcgplayer_id?: string | null;
      condition: string;
      is_foil: boolean;
      printing: string;
    }> = [];

    for (const chunk of chunkArray(requests, 100)) {
      try {
        const response = await apiClient.post<{ items: typeof previews }>(
          '/batches/preview-pricing-bulk',
          { items: chunk },
        );
        previews.push(...(response.data.items || []));
      } catch {
        const fallback = await mapWithConcurrency(chunk, 6, async (req) => {
          try {
            const response = await apiClient.post<PricingPreview>(
              '/batches/preview-pricing',
              req,
            );
            return { ...req, ...(response.data || {}) };
          } catch {
            return null;
          }
        });
        for (const preview of fallback) {
          if (preview) {
            previews.push(preview);
          }
        }
      }
    }

    if (previews.length === 0) {
      return;
    }
    setBatchItems((current) => mergeItemsWithPreviews(current, previews));
  }, []);

  // Create or load batch on mount
  useEffect(() => {
    const initBatch = async () => {
      try {
        const { getApiClient, ensureApiAuthenticated } = await import('../../services/api');
        
        await ensureApiAuthenticated();
        
        const apiClient = getApiClient() as ApiClient;
        const storedBatchId = localStorage.getItem(ACTIVE_BATCH_STORAGE_KEY);

        if (storedBatchId) {
          try {
            const batchResponse = await apiClient.get<BatchSummary>(`/batches/${storedBatchId}`);
            const existingBatch = batchResponse.data;

            if (existingBatch.status === 'draft') {
              hydrateBatch(existingBatch);
              await rehydrateBatchPolicy(apiClient, existingBatch.items || []);
              return;
            }
          } catch {
            localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
          }
        }

        // Fall back to the most recent draft batch for this user.
        const listResponse = await apiClient.get<BatchListResponseItem[]>('/batches', {
          params: { status: 'draft', limit: 1 }
        });

        if (listResponse.data && listResponse.data.length > 0) {
          const latestDraftId = listResponse.data[0].id;
          const batchResponse = await apiClient.get<BatchSummary>(`/batches/${latestDraftId}`);
          const existingBatch = batchResponse.data;
          hydrateBatch(existingBatch);
          await rehydrateBatchPolicy(apiClient, existingBatch.items || []);
        } else {
          await createNewBatch(apiClient);
        }
      } catch (err) {
        console.error('Failed to initialize batch:', err);
        setError('Failed to initialize batch. Please refresh the page.');
      }
    };
    
    initBatch();
  }, [createNewBatch, hydrateBatch, rehydrateBatchPolicy]);

  // Handle product selection from autocomplete
  const handleProductSelect = useCallback((product: Product) => {
    setSelectedProduct(product);
    setCondition(defaultCondition);
    if (pinLanguage) {
      setLanguage(defaultLanguage);
    } else if (product.language) {
      setLanguage(product.language);
    } else {
      setLanguage('EN');
    }
    setQuantity(1);
    // Pinned finish seeds from default; otherwise start at normal (preview may override).
    setFinish(pinFinish ? defaultFinish : 'normal');
    setLocationBin('');
    setPricingPreview(null);
    setError(null);
    setSuccess(null);
  }, [defaultCondition, defaultFinish, defaultLanguage, pinFinish, pinLanguage]);

  const applyCondition = useCallback((next: string) => {
    setCondition(next);
    if (pinCondition) {
      setDefaultCondition(next);
    }
  }, [pinCondition]);

  const applyFinish = useCallback((next: FinishValue) => {
    setFinish(next);
    if (pinFinish) {
      setDefaultFinish(next);
    }
  }, [pinFinish]);

  const applyLanguage = useCallback((next: string) => {
    setLanguage(next);
    if (pinLanguage) {
      setDefaultLanguage(next);
    }
  }, [pinLanguage]);

  useEffect(() => {
    const fetchPricingPreview = async () => {
      if (!selectedProduct?.product_id) {
        setPricingPreview(null);
        return;
      }

      setIsPreviewLoading(true);
      try {
        const { getApiClient } = await import('../../services/api');
        const apiClient = getApiClient();
        const response = await apiClient.post<PricingPreview>('/batches/preview-pricing', {
          ...catalogIdentityFromProduct(selectedProduct),
          condition,
          is_foil: isFoil,
          printing: finish,
        });
        const preview = response.data || null;
        setPricingPreview(preview);

        // If requested finish has no pricing, backend returns the first populated finish.
        // Do not overwrite the pinned default — only adjust the current selection.
        if (preview?.selected_finish && preview.selected_finish !== finish) {
          setFinish(preview.selected_finish);
        }
      } catch {
        setPricingPreview(null);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    fetchPricingPreview();
  }, [selectedProduct, condition, finish, isFoil]);

  // Handle quantity increment/decrement
  const incrementQuantity = () => setQuantity(prev => Math.min(prev + 1, 999));
  const decrementQuantity = () => setQuantity(prev => Math.max(prev - 1, 1));

  // Submit inventory creation
  const handleSubmit = async () => {
    if (!selectedProduct || !batchId) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;
      
      const payload = {
        ...catalogIdentityFromProduct(selectedProduct),
        card_name: selectedProduct.name,
        set_name: selectedProduct.set_name,
        card_number: selectedProduct.card_number,
        condition,
        is_foil: isFoil,
        quantity,
        location_bin: locationBin || undefined,
        language,
        printing: finish,
        estimated_cost: pricingPreview?.calculated_offer ?? undefined,
        image_url: selectedProduct.image_url,
      };

      const response = await apiClient.post<BatchItem>(`/batches/${batchId}/items`, payload);
      
      // Newest items at the top of batch review — attach live preview markets for UI
      const enriched: BatchItem = mergeIntakePricing(
        {
          ...response.data,
          image_url: response.data.image_url || selectedProduct.image_url,
        },
        pricingPreview,
      );
      setBatchItems(prev => [enriched, ...prev]);
      rememberBatchId(batchId);
      
      // Play sound effect based on price
      playPriceSound(pricingPreview?.preferred_market ?? selectedProduct.market_price);
      
      setSuccess(
        `Added ${quantity} ${condition} ${language} ${
          finish === 'signed'
            ? 'Signed '
            : finish === 'reverse_holofoil'
              ? 'Reverse Holofoil '
              : isFoil
                ? 'Foil '
                : ''
        }` +
        `${selectedProduct.name} to batch`
      );
      
      // Clear selection; keep pinned defaults for the next card
      setSelectedProduct(null);
      setQuantity(1);
      if (!pinFinish) {
        setFinish('normal');
      }
      setLocationBin('');
      setPricingPreview(null);

      setTimeout(() => {
        searchRef.current?.focusLastSearchField();
      }, 0);
      
    } catch (err: unknown) {
      console.error('Failed to add to batch:', err);
      setError(formatApiError(err, 'Failed to add to batch'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keyboard shortcuts for 10-key workflow
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedProduct || isSubmitting || isBatchSubmitting) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const activeTag = activeElement?.tagName?.toLowerCase();
      const isEditable = activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select';
      const isQuantityInput = quantityInputRef.current && activeElement === quantityInputRef.current;
      // Numpad always wins (even when focus is on condition select / location / etc.)
      const isNumpad = event.code.startsWith('Numpad');
      const allowNonNumpad = !isEditable || isQuantityInput;

      if (isEditable && !isQuantityInput && !isNumpad) return;

      const key = event.key;
      const code = event.code;

      const isPlus = code === 'NumpadAdd' || (allowNonNumpad && key === '+');
      const isMinus = code === 'NumpadSubtract' || (allowNonNumpad && key === '-');
      const isMultiply = code === 'NumpadMultiply' || (allowNonNumpad && key === '*');
      const isDivide = code === 'NumpadDivide' || (allowNonNumpad && key === '/');
      const isEnter = code === 'NumpadEnter' || (allowNonNumpad && key === 'Enter');

      if (isPlus) {
        event.preventDefault();
        incrementQuantity();
        return;
      }

      if (isMinus) {
        event.preventDefault();
        decrementQuantity();
        return;
      }

      if (isMultiply) {
        event.preventDefault();
        const cycle: FinishValue[] = ['normal', 'foil', 'etched', 'signed', 'reverse_holofoil'];
        const enabledCycle = cycle.filter((candidate) => availableFinishes.includes(candidate));
        if (enabledCycle.length === 0) return;
        const currentIdx = enabledCycle.indexOf(finish);
        const nextIdx = currentIdx >= 0 ? (currentIdx + 1) % enabledCycle.length : 0;
        applyFinish(enabledCycle[nextIdx]);
        return;
      }

      if (isDivide) {
        event.preventDefault();
        const currentIndex = CONDITIONS.findIndex((cond) => cond.value === condition);
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % CONDITIONS.length : 0;
        applyCondition(CONDITIONS[nextIndex].value);
        return;
      }

      if (isEnter) {
        event.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedProduct,
    isSubmitting,
    isBatchSubmitting,
    condition,
    finish,
    handleSubmit,
    availableFinishes,
    applyCondition,
    applyFinish,
  ]);

  // Submit entire batch to inventory
  const handleBatchSubmit = async () => {
    if (!batchId || batchItems.length === 0) {
      setError('Cannot submit empty batch');
      return;
    }

    const submitBody: {
      acquisition_mode: 'market_offer' | 'pack_open';
      pack_cost?: number;
      cards_per_pack?: number;
    } = { acquisition_mode: acquisitionMode };

    if (acquisitionMode === 'pack_open') {
      const cost = Number(packCost);
      const cards = Math.floor(Number(cardsPerPack) || 15);
      if (!Number.isFinite(cost) || cost <= 0) {
        setError('Pack cost must be a positive dollar amount for pack-open intake.');
        return;
      }
      if (!Number.isFinite(cards) || cards < 1) {
        setError('Cards per pack must be at least 1.');
        return;
      }
      submitBody.pack_cost = cost;
      submitBody.cards_per_pack = cards;
    }

    setIsBatchSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;
      
      await apiClient.post(`/batches/${batchId}/submit`, submitBody);
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      
      const costNote =
        acquisitionMode === 'pack_open' && submitBody.pack_cost != null
          ? ` Acquisition cost $${(submitBody.pack_cost / (submitBody.cards_per_pack ?? 15)).toFixed(2)}/card (pack open).`
          : '';
      setSuccess(
        `Successfully submitted batch with ${batchItems.length} item(s) to inventory!${costNote}`,
      );
      
      // Clear batch and create new one
      setBatchItems([]);
      
      // Create new batch
      await createNewBatch(apiClient);
      
      onInventoryCreated?.();
    } catch (err: unknown) {
      console.error('Failed to submit batch:', err);
      setError(formatApiError(err, 'Failed to submit batch'));
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!batchId) return;

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;

      const payload = {
        name: batchName.trim() || undefined,
      };

      const response = await apiClient.patch<BatchSummary>(`/batches/${batchId}`, payload);
      rememberBatchId(batchId);
      setBatchName(response.data.name ?? batchName);
      setSuccess('Draft saved. It will still be here when you come back.');
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to save draft:', err);
      setError(formatApiError(err, 'Failed to save draft'));
    }
  };

  const handleUpdateBatchItem = async (
    item: BatchItem,
    updates: Partial<Pick<BatchItem, 'condition' | 'quantity' | 'language' | 'printing' | 'is_foil'>>,
  ) => {
    if (!batchId) return;

    const nextCondition = updates.condition ?? item.condition;
    const nextLanguage = updates.language ?? item.language;
    const nextPrinting = updates.printing ?? item.printing;
    const nextFinish = finishFromCopy(
      updates.is_foil ?? item.is_foil,
      nextPrinting,
    );
    const nextIsFoil = updates.is_foil ?? isFoilFromFinish(nextFinish);
    const nextQuantity = updates.quantity ?? item.quantity;

    setSavingBatchItemIds((prev) => new Set(prev).add(item.id));
    setError(null);

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;

      const previewResponse = await apiClient.post<PricingPreview>('/batches/preview-pricing', {
        ...catalogIdentityFromItem(item),
        condition: nextCondition,
        is_foil: nextIsFoil,
        printing: nextFinish,
      });
      const preview = previewResponse.data;

      const payload = {
        ...catalogIdentityFromItem(item),
        card_name: item.card_name,
        set_name: item.set_name,
        card_number: item.card_number,
        condition: nextCondition,
        is_foil: nextIsFoil,
        quantity: nextQuantity,
        location_bin: item.location_bin || undefined,
        language: nextLanguage,
        printing: nextFinish,
        estimated_cost: preview?.calculated_offer ?? item.estimated_cost ?? undefined,
      };

      const response = await apiClient.patch<BatchItem>(
        `/batches/${batchId}/items/${item.id}`,
        payload,
      );

      const enriched: BatchItem = mergeIntakePricing(
        {
          ...response.data,
          image_url: response.data.image_url || item.image_url,
        },
        preview,
      );

      setBatchItems((prev) => prev.map((entry) => (entry.id === item.id ? enriched : entry)));
    } catch (err: unknown) {
      console.error('Failed to update batch item:', err);
      setError(formatApiError(err, 'Failed to update batch item'));
    } finally {
      setSavingBatchItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  // Remove item from batch
  const handleRemoveItem = async (itemId: string) => {
    if (!batchId) return;

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;
      
      await apiClient.delete(`/batches/${batchId}/items/${itemId}`);
      
      // Remove from local batch list
      setBatchItems(prev => prev.filter(item => item.id !== itemId));
      
      setSuccess('Item removed from batch');
    } catch (err: unknown) {
      console.error('Failed to remove item:', err);
      setError(formatApiError(err, 'Failed to remove item'));
    }
  };

  const handleClearBatch = async () => {
    if (!batchId) return;

    const confirmed = window.confirm(
      'Discard this draft batch and all pending items? This cannot be undone.'
    );

    if (!confirmed) return;

    try {
      const { getApiClient } = await import('../../services/api');
      const apiClient = getApiClient() as ApiClient;

      await apiClient.delete(`/batches/${batchId}`);
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      setBatchItems([]);
      setSelectedProduct(null);
      setQuantity(1);
      if (!pinFinish) {
        setFinish('normal');
      }
      setLocationBin('');
      setPricingPreview(null);
      await createNewBatch(apiClient);
      setSuccess('Draft batch cleared');
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to clear batch:', err);
      setError(formatApiError(err, 'Failed to clear batch'));
    }
  };

  // Clear selection
  const handleClear = () => {
    setSelectedProduct(null);
    setQuantity(1);
    if (!pinFinish) {
      setFinish('normal');
    }
    setLocationBin('');
    setPricingPreview(null);
    setError(null);
    setSuccess(null);
    setIsProductFlipped(false);
    setTimeout(() => {
      searchRef.current?.focusLastSearchField();
    }, 0);
  };

  return (
    <div style={styles.container}>
      {/* Default Condition / Finish with Pin */}
      <div style={styles.defaultsSection}>
        <div style={styles.defaultField}>
          <div style={styles.fieldHeader}>
            <label style={styles.label} htmlFor="defaultCondition">
              Default Condition
            </label>
            <button
              type="button"
              onClick={() => setPinCondition(!pinCondition)}
              title={pinCondition ? 'Unpin condition' : 'Pin condition for matching stacks'}
              style={{
                ...styles.persistButton,
                ...(pinCondition ? styles.persistButtonActive : {}),
              }}
            >
              {pinCondition ? 'Pinned' : 'Pin'}
            </button>
          </div>
          <select
            id="defaultCondition"
            value={defaultCondition}
            onChange={(e) => {
              setDefaultCondition(e.target.value);
              setCondition(e.target.value);
            }}
            style={styles.defaultConditionSelect}
          >
            {CONDITIONS.map((cond) => (
              <option key={cond.value} value={cond.value}>
                {cond.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.defaultField}>
          <div style={styles.fieldHeader}>
            <label style={styles.label} htmlFor="defaultFinish">
              Default Finish
            </label>
            <button
              type="button"
              onClick={() => setPinFinish(!pinFinish)}
              title={pinFinish ? 'Unpin finish' : 'Pin finish for matching stacks'}
              style={{
                ...styles.persistButton,
                ...(pinFinish ? styles.persistButtonActive : {}),
              }}
            >
              {pinFinish ? 'Pinned' : 'Pin'}
            </button>
          </div>
          <select
            id="defaultFinish"
            value={defaultFinish}
            onChange={(e) => {
              const next = e.target.value as FinishValue;
              setDefaultFinish(next);
              setFinish(next);
            }}
            style={styles.defaultConditionSelect}
          >
            {FINISHES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.defaultField}>
          <div style={styles.fieldHeader}>
            <label style={styles.label} htmlFor="defaultLanguage">
              Default Language
            </label>
            <button
              type="button"
              onClick={() => setPinLanguage(!pinLanguage)}
              title={pinLanguage ? 'Unpin language' : 'Pin language for matching stacks'}
              style={{
                ...styles.persistButton,
                ...(pinLanguage ? styles.persistButtonActive : {}),
              }}
            >
              {pinLanguage ? 'Pinned' : 'Pin'}
            </button>
          </div>
          <select
            id="defaultLanguage"
            value={defaultLanguage}
            onChange={(e) => {
              setDefaultLanguage(e.target.value);
              setLanguage(e.target.value);
            }}
            style={styles.defaultConditionSelect}
          >
            {LANGUAGES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Product Search */}
      <ProductSearchAutocomplete
        ref={searchRef}
        onProductSelect={handleProductSelect}
        defaultCondition={defaultCondition}
        initialGame={initialGame}
      />

      {/* Selected Product Form */}
      {selectedProduct && (
        <div style={styles.selectedProductSection}>
          <div style={styles.selectedProductHeader}>
            <h3 style={styles.selectedProductTitle}>Selected Product</h3>
            <button
              onClick={handleClear}
              style={styles.clearButton}
              aria-label="Clear selection"
            >
              ✕ Clear
            </button>
          </div>

          <div style={styles.productPreview}>
            {/* Large Thumbnail */}
            <div style={styles.largeThumbnailContainer}>
              <CardImage
                imageUrl={selectedProduct.image_url}
                scryfallId={selectedProduct.scryfall_id}
                reverseImageUrl={selectedProduct.reverse_image_url}
                reverseScryfallId={selectedProduct.reverse_scryfall_id}
                isDualFaced={selectedProduct.is_dual_faced}
                isFlipped={isProductFlipped}
                cardName={selectedProduct.name}
                size="fill"
                onFlip={(e) => {
                  e.stopPropagation();
                  setIsProductFlipped(!isProductFlipped);
                }}
              />
            </div>

            {/* Product Info & Controls */}
            <div style={styles.productControls}>
              <div style={styles.productInfo}>
                <div style={styles.productName}>{selectedProduct.name}</div>
                <div style={styles.productSet}>
                  {selectedProduct.set_name}
                  {selectedProduct.set_code && ` (${selectedProduct.set_code})`}
                  {selectedProduct.card_number && ` #${selectedProduct.card_number}`}
                </div>
                {selectedProduct.market_price && (
                  <div style={styles.marketPrice}>
                    Market Price: ${selectedProduct.market_price.toFixed(2)}
                  </div>
                )}
              </div>

              {/* Form Controls */}
              <div style={styles.formGrid}>
                {/* Condition Override */}
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="condition">Condition</label>
                  <select
                    id="condition"
                    value={condition}
                    onChange={(e) => applyCondition(e.target.value)}
                    style={styles.select}
                  >
                    {CONDITIONS.map((cond) => (
                      <option key={cond.value} value={cond.value}>
                        {cond.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Language */}
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="language">Language</label>
                  <select
                    id="language"
                    value={language}
                    onChange={(e) => applyLanguage(e.target.value)}
                    style={styles.select}
                  >
                    {LANGUAGES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Foil Toggle */}
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="finish">Finish</label>
                  <div style={styles.toggleContainer}>
                    <button
                      type="button"
                      onClick={() => applyFinish('normal')}
                      disabled={normalDisabled}
                      style={{
                        ...styles.toggleButton,
                        ...(finish === 'normal' ? styles.toggleButtonActive : {}),
                        ...(normalDisabled ? styles.toggleButtonDisabled : {}),
                      }}
                    >
                      Non-Foil
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFinish('foil')}
                      disabled={foilDisabled}
                      style={{
                        ...styles.toggleButton,
                        ...(finish === 'foil' ? styles.toggleButtonActive : {}),
                        ...(foilDisabled ? styles.toggleButtonDisabled : {}),
                      }}
                    >
                      Foil
                    </button>
                    {availableFinishes.includes('etched') && (
                    <button
                      type="button"
                      onClick={() => applyFinish('etched')}
                      disabled={etchedDisabled}
                      style={{
                        ...styles.toggleButton,
                        ...(finish === 'etched' ? styles.toggleButtonActive : {}),
                        ...(etchedDisabled ? styles.toggleButtonDisabled : {}),
                      }}
                    >
                      Etched
                    </button>
                    )}
                    {showSignedFinish && (
                      <button
                        type="button"
                        onClick={() => applyFinish('signed')}
                        disabled={signedDisabled}
                        style={{
                          ...styles.toggleButton,
                          ...(finish === 'signed' ? styles.toggleButtonActive : {}),
                          ...(signedDisabled ? styles.toggleButtonDisabled : {}),
                        }}
                      >
                        Signed
                      </button>
                    )}
                    {showReverseHoloFinish && (
                      <button
                        type="button"
                        onClick={() => applyFinish('reverse_holofoil')}
                        disabled={reverseHoloDisabled}
                        style={{
                          ...styles.toggleButton,
                          ...(finish === 'reverse_holofoil' ? styles.toggleButtonActive : {}),
                          ...(reverseHoloDisabled ? styles.toggleButtonDisabled : {}),
                        }}
                      >
                        Reverse Holo
                      </button>
                    )}
                  </div>
                </div>

                {/* Quantity Controls */}
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="quantity">Quantity</label>
                  <div style={styles.quantityControls}>
                    <button
                      type="button"
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      style={styles.quantityButton}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <input
                      id="quantity"
                      ref={quantityInputRef}
                      type="number"
                      min="1"
                      max="999"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 999) {
                          setQuantity(val);
                        }
                      }}
                      style={styles.quantityInput}
                    />
                    <button
                      type="button"
                      onClick={incrementQuantity}
                      disabled={quantity >= 999}
                      style={styles.quantityButton}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Location Bin */}
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="location">
                    Location (Optional)
                  </label>
                  <input
                    id="location"
                    type="text"
                    placeholder="e.g., Shelf-A-1"
                    value={locationBin}
                    onChange={(e) => setLocationBin(e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.previewPricingCard}>
                <div style={styles.previewPricingHeader}>Live Preview Pricing</div>
                {isPreviewLoading ? (
                  <div style={styles.previewPricingLoading}>Updating pricing...</div>
                ) : (
                  <>
                    <IntakePricingPanel data={pricingPreview ?? {}} />
                    {!isTcgcsvPricing(pricingPreview) && !isTcgcsvPricing(selectedProduct) ? (
                      <ManapoolSalesChart sales={pricingPreview?.manapool_recent_sales} />
                    ) : null}
                  </>
                )}
              </div>

              {/* Submit Button */}
              <div style={styles.submitSection}>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !batchId || availableFinishes.length === 0 || !availableFinishes.includes(finish)}
                  variant="primary"
                  fullWidth
                >
                  {isSubmitting ? 'Adding to Batch...' : 'Add to Batch'}
                </Button>
                <div style={styles.shortcutHint}>
                  Numpad: +/− qty · * finish · / condition · Enter add
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Review Section */}
      {batchItems.length > 0 && (
        <div style={styles.batchReviewSection}>
          <div style={styles.batchTotalsSticky}>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>TCGPlayer Total</span>
              <span style={styles.batchTotalsValue}>
                {formatPrice(batchItems.reduce((sum, item) => sum + ((item.tcgplayer_market ?? 0) * item.quantity), 0))}
              </span>
            </div>
            {batchItems.some((item) => !isTcgcsvPricing(item)) ? (
              <div style={styles.batchTotalsItem}>
                <span style={styles.batchTotalsLabel}>ManaPool Total</span>
                <span style={styles.batchTotalsValue}>
                  {formatPrice(batchItems.reduce((sum, item) => sum + ((item.manapool_market ?? 0) * item.quantity), 0))}
                </span>
              </div>
            ) : null}
            {batchItems.some((item) => !isTcgcsvPricing(item)) ? (
              <div style={styles.batchTotalsItem}>
                <span style={styles.batchTotalsLabel}>CK Buylist Total</span>
                <span style={styles.batchTotalsValue}>
                  {formatPrice(batchItems.reduce((sum, item) => sum + ((item.ck_buylist ?? 0) * item.quantity), 0))}
                </span>
              </div>
            ) : null}
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>Calculated Offer Total</span>
              <span style={styles.calculatedOfferValue}>
                {formatPrice(batchItems.reduce((sum, item) => sum + (((item.calculated_offer ?? item.estimated_cost ?? 0) * item.quantity)), 0))}
              </span>
            </div>
            <div style={styles.batchTotalsItem}>
              <span style={styles.batchTotalsLabel}>Policy Price Total</span>
              <span style={styles.calculatedOfferValue}>
                {formatPrice(batchItems.reduce((sum, item) => sum + ((item.policy_price ?? 0) * item.quantity), 0))}
              </span>
            </div>
          </div>

          <div style={styles.batchHeader}>
            <div style={styles.batchHeaderMeta}>
              <h3 style={styles.batchTitle}>Batch Review ({batchItems.length} items)</h3>
              <input
                type="text"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="Draft name"
                style={styles.batchNameInput}
              />
              <div style={styles.acquisitionRow}>
                <label style={styles.acquisitionLabel}>
                  Acquisition
                  <select
                    value={acquisitionMode}
                    onChange={(e) =>
                      setAcquisitionMode(e.target.value as 'market_offer' | 'pack_open')
                    }
                    style={styles.acquisitionSelect}
                    disabled={isBatchSubmitting}
                  >
                    <option value="market_offer">Market offer %</option>
                    <option value="pack_open">Pack open (cost ÷ cards)</option>
                  </select>
                </label>
                {acquisitionMode === 'pack_open' && (
                  <>
                    <label style={styles.acquisitionLabel}>
                      Pack cost ($)
                      <input
                        type="text"
                        inputMode="decimal"
                        value={packCost}
                        onChange={(e) => setPackCost(e.target.value)}
                        placeholder="12.00"
                        style={styles.batchNameInput}
                        disabled={isBatchSubmitting}
                      />
                    </label>
                    <label style={styles.acquisitionLabel}>
                      Cards / pack
                      <input
                        type="text"
                        inputMode="numeric"
                        value={cardsPerPack}
                        onChange={(e) => setCardsPerPack(e.target.value)}
                        style={styles.batchNameInput}
                        disabled={isBatchSubmitting}
                      />
                    </label>
                    {Number(packCost) > 0 && Number(cardsPerPack) > 0 && (
                      <span style={styles.acquisitionHint}>
                        ≈ ${(Number(packCost) / Number(cardsPerPack)).toFixed(2)} / card
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div style={styles.batchHeaderActions}>
              <Button
                onClick={handleSaveDraft}
                disabled={isBatchSubmitting || !batchId}
                variant="secondary"
              >
                Save Draft
              </Button>
              <Button
                onClick={handleClearBatch}
                disabled={isBatchSubmitting}
                variant="secondary"
              >
                Clear Batch
              </Button>
              <Button
                onClick={handleBatchSubmit}
                disabled={isBatchSubmitting}
                variant="primary"
              >
                {isBatchSubmitting ? 'Submitting...' : 'Submit Batch to Inventory'}
              </Button>
            </div>
          </div>
          
          <div style={styles.batchItemsList}>
            {batchItems.map((item) => (
              <div key={item.id} style={styles.batchItem}>
                {/* Thumbnail */}
                <div style={styles.batchItemThumbnailContainer}>
                  {item.image_url ? (
                    <CardImage
                      imageUrl={item.image_url}
                      cardName={item.card_name}
                      size="fill"
                    />
                  ) : (
                    <div
                      style={styles.batchItemPlaceholder}
                      title="Card image could not be rebuilt from MTGJSON/Scryfall identifiers"
                    >
                      <span style={styles.placeholderText}>Image unavailable</span>
                    </div>
                  )}
                </div>

                {/* Item Details */}
                <div style={styles.batchItemInfo}>
                  <div style={styles.batchItemName}>
                    {item.card_name}
                  </div>
                  <InventoryCopyLookupLink
                    productLine={item.product_line}
                    productName={item.card_name}
                    setName={item.set_name}
                    cardNumber={item.card_number}
                    condition={item.condition}
                    printing={item.printing}
                    language={item.language}
                  />
                  <div style={styles.batchItemDetails}>
                    {item.set_name && <span>{item.set_name}</span>}
                    {item.card_number && <span> #{item.card_number}</span>}
                  </div>
                  <div style={styles.batchItemControls}>
                    <label style={styles.batchControlLabel}>
                      Cond
                      <select
                        value={item.condition}
                        onChange={(e) => handleUpdateBatchItem(item, { condition: e.target.value })}
                        style={styles.batchControlSelect}
                        disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting}
                        aria-label="Condition"
                      >
                        {CONDITIONS.map((cond) => (
                          <option key={cond.value} value={cond.value}>{cond.value}</option>
                        ))}
                      </select>
                    </label>
                    <label style={styles.batchControlLabel}>
                      Finish
                      <select
                        value={finishFromCopy(item.is_foil, item.printing)}
                        onChange={(e) => {
                          const nextFinish = e.target.value as FinishValue;
                          handleUpdateBatchItem(item, {
                            printing: nextFinish,
                            is_foil: isFoilFromFinish(nextFinish),
                          });
                        }}
                        style={styles.batchControlSelect}
                        disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting}
                        aria-label="Finish"
                      >
                        {FINISHES.map((finishOption) => (
                          <option key={finishOption.value} value={finishOption.value}>
                            {finishOption.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={styles.batchControlLabel}>
                      Lang
                      <select
                        value={item.language || 'EN'}
                        onChange={(e) => handleUpdateBatchItem(item, { language: e.target.value })}
                        style={styles.batchControlSelect}
                        disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting}
                        aria-label="Language"
                      >
                        {LANGUAGES.map((lang) => (
                          <option key={lang.value} value={lang.value}>{lang.value}</option>
                        ))}
                      </select>
                    </label>
                    <label style={styles.batchControlLabel}>
                      Qty
                      <div style={styles.batchQuantityControl}>
                        <button
                          type="button"
                          onClick={() => handleUpdateBatchItem(item, { quantity: Math.max(1, item.quantity - 1) })}
                          style={styles.batchQuantityButton}
                          disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting || item.quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={999}
                          defaultValue={item.quantity}
                          key={`${item.id}-${item.quantity}`}
                          onBlur={(e) => {
                            const parsed = Number(e.target.value);
                            if (
                              Number.isFinite(parsed)
                              && parsed >= 1
                              && parsed <= 999
                              && Math.floor(parsed) !== item.quantity
                            ) {
                              handleUpdateBatchItem(item, { quantity: Math.floor(parsed) });
                            }
                          }}
                          style={styles.batchQuantityInput}
                          disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting}
                          aria-label="Quantity"
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateBatchItem(item, { quantity: Math.min(999, item.quantity + 1) })}
                          style={styles.batchQuantityButton}
                          disabled={savingBatchItemIds.has(item.id) || isBatchSubmitting || item.quantity >= 999}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </label>
                    {item.location_bin && (
                      <span style={styles.batchLocationHint}>Loc: {item.location_bin}</span>
                    )}
                    {savingBatchItemIds.has(item.id) && (
                      <span style={styles.batchSavingHint}>Saving…</span>
                    )}
                  </div>
                </div>

                {/* Pricing: always show policy signals (GET live preview or client rehydrate). */}
                <div style={styles.batchItemPricing}>
                  {item.estimated_cost != null && (
                    <>
                      <div style={styles.costPerUnit}>
                        {formatPrice(item.estimated_cost)} each
                      </div>
                      <div style={styles.costTotal}>
                        {formatPrice(item.estimated_cost * item.quantity)} total
                      </div>
                    </>
                  )}
                  <IntakePricingPanel
                    compact
                    data={{
                      ...item,
                      calculated_offer: item.calculated_offer ?? item.estimated_cost,
                    }}
                  />
                  {!isTcgcsvPricing(item) ? (
                    <ManapoolSalesChart sales={item.manapool_recent_sales} />
                  ) : null}
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => handleRemoveItem(item.id)}
                  style={styles.removeButton}
                  aria-label="Remove item"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div style={styles.successMessage}>
          ✓ {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={styles.errorMessage}>
          ✗ {error}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.lg,
    padding: spacing.lg,
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    marginBottom: spacing['2xl'],
  },
  defaultConditionSection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
    maxWidth: '420px',
  },
  defaultsSection: {
    display: 'grid' as const,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    maxWidth: '960px',
  },
  defaultField: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  fieldHeader: {
    display: 'flex' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  persistButton: {
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.textSecondary,
    cursor: 'pointer',
    opacity: 0.8,
    transition: `all ${transitions.base}`,
  },
  persistButtonActive: {
    opacity: 1,
    borderColor: colors.primary,
    color: colors.primary,
  },
  label: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  select: {
    fontSize: typography.body.fontSize,
    padding: `${spacing.sm} ${spacing.md}`,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
    cursor: 'pointer',
    height: spacing.xl,
    lineHeight: '1.2',
    boxSizing: 'border-box' as const,
  },
  defaultConditionSelect: {
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    padding: `0 ${spacing.md}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.gray100,
    color: colors.text,
    outline: 'none',
    cursor: 'pointer',
    height: '40px',
    lineHeight: 'normal',
    boxSizing: 'border-box' as const,
    width: '100%',
    maxWidth: '420px',
  },
  selectedProductSection: {
    marginTop: spacing.lg,
    padding: spacing.lg,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
  },
  selectedProductHeader: {
    display: 'flex' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  selectedProductTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    margin: 0,
  },
  clearButton: {
    fontSize: typography.small.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.textSecondary,
    cursor: 'pointer',
    transition: `all ${transitions.base}`,
  },
  productPreview: {
    display: 'grid' as const,
    gridTemplateColumns: '300px 1fr',
    gap: spacing['2xl'],
  },
  largeThumbnailContainer: {
    width: '300px',
    aspectRatio: '5/7',
    overflow: 'hidden' as const,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
  },
  largeThumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  placeholderThumbnail: {
    width: '100%',
    height: '100%',
    backgroundColor: '#333',
    display: 'flex' as const,
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: typography.body.fontSize,
    color: '#999',
  },
  productControls: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  productInfo: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  productName: {
    fontSize: typography.h2.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  productSet: {
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
  },
  marketPrice: {
    fontSize: typography.small.fontSize,
    color: colors.success,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  previewPricingCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    padding: spacing.md,
  },
  previewPricingHeader: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  previewPricingLoading: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  formGrid: {
    display: 'grid' as const,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
  },
  field: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  input: {
    fontSize: typography.body.fontSize,
    padding: `${spacing.sm} ${spacing.md}`,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
    transition: `border-color ${transitions.base} ease-in-out`,
    height: spacing.xl,
    boxSizing: 'border-box' as const,
  },
  toggleContainer: {
    display: 'flex' as const,
    gap: spacing.xs,
  },
  toggleButton: {
    flex: 1,
    fontSize: typography.small.fontSize,
    padding: `${spacing.sm} ${spacing.md}`,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    cursor: 'pointer',
    transition: `all ${transitions.base}`,
  },
  toggleButtonActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
    color: '#fff',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  toggleButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  quantityControls: {
    display: 'flex' as const,
    alignItems: 'center',
    gap: spacing.xs,
  },
  quantityButton: {
    fontSize: typography.h2.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
    width: spacing.xl,
    height: spacing.xl,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    cursor: 'pointer',
    transition: `all ${transitions.base}`,
    display: 'flex' as const,
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box' as const,
  },
  quantityInput: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    width: '80px',
    padding: `${spacing.sm} ${spacing.md}`,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    textAlign: 'center' as const,
    outline: 'none',
    height: spacing.xl,
    boxSizing: 'border-box' as const,
  },
  submitSection: {
    marginTop: spacing.md,
  },
  shortcutHint: {
    marginTop: spacing.sm,
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    textAlign: 'center' as const,
  },
  successMessage: {
    padding: spacing.md,
    backgroundColor: '#d4edda',
    border: `1px solid ${colors.success}`,
    borderRadius: borderRadius.md,
    color: colorExtensions.successDark,
    fontSize: typography.body.fontSize,
  },
  errorMessage: {
    padding: spacing.md,
    backgroundColor: '#f8d7da',
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.md,
    color: colorExtensions.errorDark,
    fontSize: typography.body.fontSize,
  },
  batchReviewSection: {
    marginTop: spacing.lg,
    padding: spacing.lg,
    backgroundColor: colors.surface,
    border: `2px solid ${colors.primary}`,
    borderRadius: borderRadius.lg,
  },
  batchTotalsSticky: {
    position: 'sticky' as const,
    top: spacing.md,
    zIndex: 2,
    display: 'grid' as const,
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: spacing.sm,
    padding: spacing.sm,
    marginBottom: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: `${colors.primaryLight}25`,
    border: `1px solid ${colors.primary}`,
  },
  batchTotalsItem: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
    padding: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.sm,
  },
  batchTotalsLabel: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  batchTotalsValue: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  batchHeader: {
    display: 'flex' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  batchHeaderMeta: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
    minWidth: '280px',
    flex: 1,
    marginRight: spacing.md,
  },
  batchNameInput: {
    fontSize: typography.small.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
    maxWidth: '360px',
    boxSizing: 'border-box' as const,
  },
  acquisitionRow: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: spacing.sm,
    alignItems: 'flex-end',
    marginTop: spacing.xs,
  },
  acquisitionLabel: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '2px',
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  acquisitionSelect: {
    fontSize: typography.small.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.text,
  },
  acquisitionHint: {
    fontSize: typography.small.fontSize,
    color: colors.primary,
    fontWeight: typographyFlat.fontWeight.medium,
    paddingBottom: spacing.xs,
  },
  batchHeaderActions: {
    display: 'flex' as const,
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  batchTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    margin: 0,
  },
  batchItemsList: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  batchItem: {
    display: 'flex' as const,
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    transition: `all ${transitions.base}`,
  },
  batchItemThumbnailContainer: {
    width: '60px',
    height: '84px',
    flexShrink: 0,
    overflow: 'hidden' as const,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: '#333',
  },
  batchItemThumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  batchItemPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex' as const,
    alignItems: 'center',
    justifyContent: 'center',
  },
  batchItemInfo: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: spacing.xs,
    flex: 1,
  },
  batchItemName: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    display: 'flex' as const,
    alignItems: 'center',
    gap: spacing.sm,
  },
  batchItemDetails: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  batchItemControls: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  batchControlLabel: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '2px',
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  batchControlSelect: {
    fontSize: typography.caption.fontSize,
    padding: `${spacing.xs} ${spacing.xs}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    minWidth: '64px',
  },
  batchQuantityControl: {
    display: 'flex' as const,
    alignItems: 'center',
    gap: spacing.xs,
  },
  batchQuantityButton: {
    width: spacing.lg,
    height: spacing.lg,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    lineHeight: 1,
  },
  batchQuantityInput: {
    width: '48px',
    padding: `${spacing.xs}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    textAlign: 'center' as const,
    fontSize: typography.small.fontSize,
  },
  batchLocationHint: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    alignSelf: 'center' as const,
  },
  batchSavingHint: {
    fontSize: typography.caption.fontSize,
    color: colors.primary,
    alignSelf: 'center' as const,
    fontStyle: 'italic' as const,
  },
  batchItemPricing: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: spacing.xs,
    minWidth: '150px',
  },
  costPerUnit: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  costTotal: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.success,
  },
  pricingGrid: {
    display: 'grid' as const,
    gridTemplateColumns: 'auto auto',
    gap: `${spacing.xs} ${spacing.sm}`,
    alignItems: 'center',
  },
  pricingLabel: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  pricingValue: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  calculatedOfferValue: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
    color: colors.success,
  },
  removeButton: {
    fontSize: typography.body.fontSize,
    width: spacing.lg,
    height: spacing.lg,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.textSecondary,
    cursor: 'pointer',
    transition: `all ${transitions.base}`,
    display: 'flex' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  foilBadge: {
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.bold,
    padding: `2px ${spacing.xs}`,
    backgroundColor: '#FFD700',
    color: '#000',
    borderRadius: borderRadius.sm,
  },
  conditionBadge: {
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
};
